import { Navigate, useLocation, Outlet, matchPath } from 'react-router';

import { useAuth } from '../../hooks/useAuth';
import { ErrorBanner } from '../layout/error-banner';
import { ROUTE_PERMISSIONS } from '../../guards/route-permissions';
import type { AllowedOrgType } from '../../guards/permissions';
import { FullPageLoader } from './loading';

type ProtectedRouteProps = {
  onboardingPath?: string;
  organizationPickerPath?: string;
};

export function ProtectedRoute({
  onboardingPath = '/onboarding/create-organization',
  organizationPickerPath = '/onboarding/select-organization',
}: ProtectedRouteProps) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.loading) return <FullPageLoader />;

  if (!auth.isAuthenticated) return <Navigate to="/auth" replace />;

  const isOnboardingRoute =
    location.pathname === onboardingPath || location.pathname.startsWith(`${onboardingPath}/`);
  const isPickerRoute =
    location.pathname === organizationPickerPath ||
    location.pathname.startsWith(`${organizationPickerPath}/`);
  const hasMultipleOrgs = auth.availableOrganizations.length > 1;
  const hasPendingInvites = auth.pendingInvitations.length > 0;

  if (!auth.organization && !isOnboardingRoute && !isPickerRoute) {
    // Send org-less users to the picker when they have orgs to choose from OR a
    // pending invitation to accept; only fall back to create-org when neither
    // exists. The picker itself redirects to create-org if it has nothing to show.
    if (hasMultipleOrgs || hasPendingInvites) {
      return <Navigate to={organizationPickerPath} replace />;
    }
    return <Navigate to={onboardingPath} replace />;
  }

  const routePerm = Object.entries(ROUTE_PERMISSIONS).find(([pattern]) =>
    matchPath(pattern, location.pathname),
  );
  const allowedOrgTypes = routePerm?.[1]?.allowedOrgTypes;

  if (allowedOrgTypes && allowedOrgTypes.length > 0 && auth.organization?.type) {
    const orgType = auth.organization.type;
    if (!allowedOrgTypes.includes(orgType as AllowedOrgType)) {
      return (
        <ErrorBanner
          title="Access restricted"
          message="This section is not available for your organization"
          action={{ text: 'Go to Dashboard', href: '/' }}
        />
      );
    }
  }

  return <Outlet />;
}
